import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { buildDatePresetRange } from '~/services/TimeService';

import {
  COLOR_WHITE,
  CS_FEATURES,
  CS_FEATURE_MAP,
  DEFAULT_CS_FEATURE_IDS,
  MAX_SELECTED_CS_FEATURES,
} from './features/registry';

type Props = {
  selectedFeatureIds: string[];
  selectedTeamLeader: string;
  selectedDepartment: string;
  selectedSubDepartment: string;
  startAt: string;
  endAt: string;
  teamLeaders: string[];
  departments: string[];
  subDepartments: string[];
  updateUrlParams: (updates: Record<string, string>) => void;
};

const MAX_VISIBLE_SELECTED_FEATURE_CHIPS = 2;

export default function CsDashboardFilters({
  selectedFeatureIds,
  selectedTeamLeader,
  selectedDepartment,
  selectedSubDepartment,
  startAt,
  endAt,
  teamLeaders,
  departments,
  subDepartments,
  updateUrlParams,
}: Props) {
  function applyDatePreset(amount: number, unit: 'day' | 'month') {
    const { startAt, endAt } = buildDatePresetRange(amount, unit);
    updateUrlParams({ start_at: startAt, end_at: endAt });
  }

  const hasNonDefaultFeatures =
    selectedFeatureIds.length !== DEFAULT_CS_FEATURE_IDS.length ||
    selectedFeatureIds.some((id) => !DEFAULT_CS_FEATURE_IDS.includes(id));

  const hasActiveRosterFilters = Boolean(selectedTeamLeader || selectedDepartment || selectedSubDepartment);
  const hasAnyActiveFilters = Boolean(startAt || endAt || hasNonDefaultFeatures || hasActiveRosterFilters);
  const activeFilterCount = [
    hasNonDefaultFeatures,
    selectedTeamLeader,
    selectedDepartment,
    selectedSubDepartment,
    startAt || endAt,
  ].filter(Boolean).length;

  return (
    <Box mt={4}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Filters</Typography>
          {activeFilterCount > 0 && <Chip label={`${activeFilterCount} active`} size="small" />}
        </Stack>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <FormControl sx={{ width: 420, maxWidth: '100%' }} size="small">
              <InputLabel id="features-label">Features</InputLabel>
              <Select
                labelId="features-label"
                multiple
                value={selectedFeatureIds}
                onChange={(e) => {
                  const value = e.target.value;
                  const nextSelectedFeatureIds = typeof value === 'string' ? value.split(',') : value;
                  if (nextSelectedFeatureIds.length > MAX_SELECTED_CS_FEATURES) return;
                  updateUrlParams({
                    features:
                      nextSelectedFeatureIds.length > 0
                        ? nextSelectedFeatureIds.join(',')
                        : DEFAULT_CS_FEATURE_IDS.join(','),
                  });
                }}
                input={<OutlinedInput label="Features" />}
                renderValue={(selected) => {
                  const selectedIds = selected as string[];
                  const visibleFeatureIds = selectedIds.slice(0, MAX_VISIBLE_SELECTED_FEATURE_CHIPS);
                  const hiddenFeatureCount = Math.max(0, selectedIds.length - visibleFeatureIds.length);

                  return (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: 'hidden' }}>
                      {visibleFeatureIds.map((id) => {
                        const feature = CS_FEATURE_MAP[id];
                        return feature ? (
                          <Chip
                            key={id}
                            label={feature.label}
                            size="small"
                            sx={{ bgcolor: feature.color, color: COLOR_WHITE, height: 22, maxWidth: 180 }}
                          />
                        ) : null;
                      })}
                      {hiddenFeatureCount > 0 && (
                        <Chip label={`+${hiddenFeatureCount} more`} size="small" sx={{ height: 22 }} />
                      )}
                    </Stack>
                  );
                }}
              >
                {CS_FEATURES.map((feature) => (
                  <MenuItem
                    key={feature.id}
                    value={feature.id}
                    disabled={
                      selectedFeatureIds.length >= MAX_SELECTED_CS_FEATURES && !selectedFeatureIds.includes(feature.id)
                    }
                  >
                    <Checkbox checked={selectedFeatureIds.includes(feature.id)} size="small" />
                    <ListItemText primary={feature.label} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {selectedFeatureIds.length}/{MAX_SELECTED_CS_FEATURES} selected
              </FormHelperText>
            </FormControl>

            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel id="team-leader-label" shrink>
                Team Leader
              </InputLabel>
              <Select
                labelId="team-leader-label"
                value={selectedTeamLeader}
                label="Team Leader"
                onChange={(e) => updateUrlParams({ teamLeader: e.target.value })}
                displayEmpty
                renderValue={(selected) => (selected ? String(selected) : 'All Agents')}
              >
                <MenuItem value="">
                  <Typography fontStyle="italic">All Agents</Typography>
                </MenuItem>
                {teamLeaders.map((leader) => (
                  <MenuItem key={leader} value={leader}>
                    {leader}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 220 }} size="small">
              <InputLabel id="department-label" shrink>
                Department
              </InputLabel>
              <Select
                labelId="department-label"
                value={selectedDepartment}
                label="Department"
                onChange={(e) => updateUrlParams({ department: e.target.value, subDepartment: '' })}
                disabled={departments.length === 0}
                displayEmpty
                renderValue={(selected) => (selected ? String(selected) : 'All Departments')}
              >
                <MenuItem value="">
                  <Typography fontStyle="italic">All Departments</Typography>
                </MenuItem>
                {departments.map((department) => (
                  <MenuItem key={department} value={department}>
                    {department}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 240 }} size="small">
              <InputLabel id="subdepartment-label" shrink>
                Subdepartment
              </InputLabel>
              <Select
                labelId="subdepartment-label"
                value={selectedSubDepartment}
                label="Subdepartment"
                onChange={(e) => updateUrlParams({ subDepartment: e.target.value })}
                disabled={!selectedDepartment || subDepartments.length === 0}
                displayEmpty
                renderValue={(selected) => {
                  if (!selectedDepartment) return 'Select Department';
                  return selected ? String(selected) : 'All Subdepartments';
                }}
              >
                <MenuItem value="">
                  <Typography fontStyle="italic">All Subdepartments</Typography>
                </MenuItem>
                {subDepartments.map((subDepartment) => (
                  <MenuItem key={subDepartment} value={subDepartment}>
                    {subDepartment}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <TextField
              label="Start date"
              type="date"
              size="small"
              value={startAt}
              onChange={(e) => updateUrlParams({ start_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <TextField
              label="End date"
              type="date"
              size="small"
              value={endAt}
              onChange={(e) => updateUrlParams({ end_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />

            <Button variant="outlined" size="small" onClick={() => applyDatePreset(14, 'day')}>
              Last 2 weeks
            </Button>
            <Button variant="outlined" size="small" onClick={() => applyDatePreset(28, 'day')}>
              Last 4 weeks
            </Button>
            <Button variant="outlined" size="small" onClick={() => applyDatePreset(3, 'month')}>
              Last 3 months
            </Button>

            {hasAnyActiveFilters && (
              <Button
                variant="text"
                onClick={() =>
                  updateUrlParams({
                    features: '',
                    start_at: '',
                    end_at: '',
                    teamLeader: '',
                    department: '',
                    subDepartment: '',
                  })
                }
              >
                Clear filters
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
